import React, { type FunctionComponent, useCallback } from 'react'
import styled from '@emotion/styled'
import { Modal } from '@retailer-platform/shared-components/src/common/modal/Modal'
import merge from 'lodash/merge'
import { type PartnerPickRecurringShiftsRecurringShift } from 'domains/partner-pick-devices/api'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { PartnerPickRecurringShiftsForm } from './PartnerPickRecurringShiftsForm'
import { type PartnerPickRecurringShiftsModalProps } from './recurringShift.types'
import { usePartnerPickRecurringShiftsNotificationsHooks } from './usePartnerPickRecurringShiftsNotifications.hooks'

const StyledContainer = styled.div({
  position: 'absolute',
  border: '1px solid red',
  padding: '15px',
  borderRadius: '12px',
})

export const PartnerPickShiftsModal: FunctionComponent<PartnerPickRecurringShiftsModalProps> = ({
  onCancel,
  onSave,
  onDelete,
  getLocalShopperDetails,
  deleteNonRecurringShift,
  modalStyles,
  ...props
}) => {
  const { notify } = usePartnerPickRecurringShiftsNotificationsHooks()
  const {
    shiftsState: { isShiftsModalOpen },
  } = useShiftsStateContext()
  // this is called after Shift CRUD is done and then parent onSave updates state for visual UI change
  const onShiftSave = useCallback(
    (
      shift: PartnerPickRecurringShiftsRecurringShift,
      shopperId: number,
      shiftId: number,
      conflictIds: string[]
    ) => {
      onSave(shift, shopperId, shiftId, conflictIds)
      notify(shift)
      onCancel()
    },
    [onCancel, notify, onSave]
  )

  const onApiError = useCallback(() => {
    notify(null, 'generatedReports.newReport.errors.loadingError')
    onCancel()
  }, [onCancel, notify])

  const onShiftDelete = useCallback(
    (shopperId: number, shiftId: number) => {
      onDelete(shopperId, shiftId)
      notify(null, 'shiftsDomain.notifications.recurring.deleted')
      onCancel()
    },
    [onCancel, notify, onDelete]
  )

  const customStyles = {
    content: {
      borderRadius: '12px',
    },
  }
  const styleOverrides = merge(modalStyles, customStyles)

  return (
    <StyledContainer>
      <Modal
        isOpen={isShiftsModalOpen}
        maxHeight={900}
        maxWidth={344}
        hideOverlay
        hideCloseButton
        styleOverrides={styleOverrides}
      >
        <PartnerPickRecurringShiftsForm
          deleteNonRecurringShift={deleteNonRecurringShift}
          getLocalShopperDetails={getLocalShopperDetails}
          onCancel={onCancel}
          onSave={onShiftSave}
          onApiError={onApiError}
          onDelete={onShiftDelete}
          {...props}
        />
      </Modal>
    </StyledContainer>
  )
}
